import { NgClass } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewEncapsulation,
    ViewRef
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { IconsType } from '../enums/icons.type';
import { NotificationAnimationType } from '../enums/notification-animation-type.enum';
import { Notification } from '../interfaces/notification.type';
import { NotificationsService } from '../services/notifications.service';

@Component({
    selector: 'toast-notification',
    imports: [
        NgClass
    ],
    encapsulation: ViewEncapsulation.None,
    styles: [`
        .simple-notification {
            width: 100%;
            padding: 10px 20px;
            box-sizing: border-box;
            position: relative;
            float: left;
            margin-bottom: 10px;
            color: #fff;
            cursor: pointer;
            transition: all 0.5s;
            min-height: 70px;
            border-radius: 0.25rem;
        }

        /* item.state is the animation type while shown and gets an "Out" suffix while being removed */
        @keyframes sn-fade-in { from { opacity: 0; } }
        @keyframes sn-fade-out { to { opacity: 0; } }
        .simple-notification[data-state=fade] { animation: sn-fade-in 400ms ease-in-out; }
        .simple-notification[data-state=fadeOut] { animation: sn-fade-out 300ms ease-in-out forwards; }

        @keyframes sn-from-top-in { from { opacity: 0; transform: translateY(-5%); } }
        @keyframes sn-from-top-out { to { opacity: 0; transform: translateY(5%); } }
        .simple-notification[data-state=fromTop] { animation: sn-from-top-in 400ms ease-in-out; }
        .simple-notification[data-state=fromTopOut] { animation: sn-from-top-out 300ms ease-in-out forwards; }

        @keyframes sn-from-right-in { from { opacity: 0; transform: translateX(5%); } }
        @keyframes sn-from-right-out { to { opacity: 0; transform: translateX(-5%); } }
        .simple-notification[data-state=fromRight] { animation: sn-from-right-in 400ms ease-in-out; }
        .simple-notification[data-state=fromRightOut] { animation: sn-from-right-out 300ms ease-in-out forwards; }

        @keyframes sn-from-bottom-in { from { opacity: 0; transform: translateY(5%); } }
        @keyframes sn-from-bottom-out { to { opacity: 0; transform: translateY(-5%); } }
        .simple-notification[data-state=fromBottom] { animation: sn-from-bottom-in 400ms ease-in-out; }
        .simple-notification[data-state=fromBottomOut] { animation: sn-from-bottom-out 300ms ease-in-out forwards; }

        @keyframes sn-from-left-in { from { opacity: 0; transform: translateX(-5%); } }
        @keyframes sn-from-left-out { to { opacity: 0; transform: translateX(5%); } }
        .simple-notification[data-state=fromLeft] { animation: sn-from-left-in 400ms ease-in-out; }
        .simple-notification[data-state=fromLeftOut] { animation: sn-from-left-out 300ms ease-in-out forwards; }

        @keyframes sn-scale-in { from { opacity: 0; transform: scale(0); } }
        @keyframes sn-scale-out { to { opacity: 0; transform: scale(0); } }
        .simple-notification[data-state=scale] { animation: sn-scale-in 400ms ease-in-out; }
        .simple-notification[data-state=scaleOut] { animation: sn-scale-out 400ms ease-in-out forwards; }

        @keyframes sn-rotate-in { from { opacity: 0; transform: rotate(5deg); } }
        @keyframes sn-rotate-out { to { opacity: 0; transform: rotate(-5deg); } }
        .simple-notification[data-state=rotate] { animation: sn-rotate-in 400ms ease-in-out; }
        .simple-notification[data-state=rotateOut] { animation: sn-rotate-out 400ms ease-in-out forwards; }

        .simple-notification .sn-title,
        .simple-notification .sn-content,
        .simple-notification .sn-html {
            margin: 0;
        }

        .simple-notification .sn-title {
            line-height: 30px;
            font-size: 18px;
            font-weight: bold;
        }

        .simple-notification .sn-content {
            font-size: 14px;
            line-height: 20px;
        }

        .simple-notification.has-icon .sn-title,
        .simple-notification.has-icon .sn-content,
        .simple-notification.has-icon .sn-html {
            padding: 0 50px 0 0;
        }

        .simple-notification .icon {
            position: absolute;
            box-sizing: border-box;
            top: 0;
            right: 0;
            width: 45px;
            height: 45px;
            padding: 10px;
        }

        .simple-notification .icon.icon-hover:hover {
            opacity: 0.5;
        }

        .simple-notification .icon svg {
            fill: #fff;
            width: 100%;
            height: 100%;
        }

        .simple-notification .icon svg g {
            fill: #fff;
        }

        .simple-notification.rtl-mode.has-icon .sn-title,
        .simple-notification.rtl-mode.has-icon .sn-content,
        .simple-notification.rtl-mode.has-icon .sn-html {
            padding: 0 0 0 50px;
        }

        .simple-notification.rtl-mode {
            direction: rtl;
        }

        .simple-notification.rtl-mode .sn-content {
            padding: 0 0 0 50px;
        }

        .simple-notification.rtl-mode svg {
            left: 0;
            right: auto;
        }

        .simple-notification .sn-progress-loader {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 5px;
        }

        .simple-notification .sn-progress-loader span {
            float: left;
            height: 100%;
        }

        .simple-notification.warn div .sn-title,
        .simple-notification.warn div .sn-content,
        .simple-notification.warn div .sn-html { color: #444; }

    `],
    template: `
        <div class="simple-notification"
             [attr.data-state]="item.state"
             (click)="onClick($event)"
             [class]="theClass"
             [ngClass]="{
            'alert': item.type === 'alert',
            'error': item.type === 'error',
            'warn': item.type === 'warn',
            'success': item.type === 'success',
            'info': item.type === 'info',
        }"
             (mouseenter)="onEnter()"
             (mouseleave)="onLeave()">

            <div>
                <div class="sn-title" [innerHTML]="title"></div>
                <div class="sn-content" [innerHTML]="content"></div>

                @if (item.icon !== IconsType.none) {
                    <div class="icon mr-2">
                        @switch (item.icon) {
                            @case (IconsType.alert) {
                                <i class="fa fa-clock-o fa-2x" aria-hidden="true"></i>
                            }
                            @case (IconsType.error) {
                                <i class="fa fa-exclamation-circle fa-2x" aria-hidden="true"></i>
                            }
                            @case (IconsType.info) {
                                <i class="fa fa-info-circle fa-2x" aria-hidden="true"></i>
                            }
                            @case (IconsType.warn) {
                                <i class="fa fa-exclamation-triangle fa-2x" aria-hidden="true"></i>
                            }
                            @case (IconsType.success) {
                                <i class="fa fa-check fa-2x" aria-hidden="true"></i>
                            }
                            @case (IconsType.successCircle) {
                                <i class="fa fa-check-circle text-success fa-2x" aria-hidden="true"></i>
                            }
                        }
                    </div>
                }

                @if (showProgressBar) {
                    <div class="sn-progress-loader">
                        <span [style.width.%]="progressWidth"></span>
                    </div>
                }
            </div>
        </div>
    `,
})

export class NotificationComponent implements OnInit, OnDestroy {

    @Input() timeOut: number;

    @Input() showProgressBar: boolean;

    @Input() pauseOnHover: boolean;

    @Input() clickToClose: boolean;

    @Input() clickIconToClose: boolean;

    @Input() maxLength: number;

    @Input() theClass: string;

    @Input() rtl: boolean;

    @Input() animate: NotificationAnimationType;

    @Input() position: number;

    @Input() item: Notification;


    [key: string]: any;

    // Progress bar variables
    title: any;

    content: any;

    titleIsTemplate = false;

    contentIsTemplate = false;

    htmlIsTemplate = false;

    progressWidth = 0;

    safeSvg: SafeHtml;

    safeInputHtml: SafeHtml;

    private stopTime = false;

    private timer: any;

    private framesPerSecond = 40;

    private sleepTime: number;

    private startTime: number;

    private endTime: number;

    private pauseStart: number;

    private icon: string;

    constructor(
        private notificationService: NotificationsService,
        private domSanitizer: DomSanitizer,
        private cd: ChangeDetectorRef,
        private zone: NgZone
    ) {}

    ngOnInit() {
        if (this.item.override) {
            this.attachOverrides();
        }

        if (this.animate) {
            this.item.state = this.animate;
        }

        if (this.timeOut !== 0) {
            this.startTimeOut();
        }

        this.contentType(this.item.title, 'title');
        this.contentType(this.item.content, 'content');
        this.contentType(this.item.html, 'html');

        this.safeSvg = this.domSanitizer.bypassSecurityTrustHtml(this.icon || this.item.icon);
        this.safeInputHtml = this.domSanitizer.bypassSecurityTrustHtml(this.item.html);
    }

    ngOnDestroy() {
        clearTimeout(this.timer);
        this.cd.detach();
    }

    startTimeOut(): void {
        this.sleepTime = 1000 / this.framesPerSecond /* ms */;
        this.startTime = new Date().getTime();
        this.endTime = this.startTime + this.timeOut;
        this.zone.runOutsideAngular(() => this.timer = setTimeout(this.instance, this.sleepTime));
    }

    onEnter() {
        if (this.pauseOnHover) {
            this.stopTime = true;
            this.pauseStart = new Date().getTime();
        }
    }

    onLeave() {
        if (this.pauseOnHover) {
            this.stopTime = false;
            this.startTime += (new Date().getTime() - this.pauseStart);
            this.endTime += (new Date().getTime() - this.pauseStart);
            this.zone.runOutsideAngular(() => setTimeout(this.instance, this.sleepTime));
        }
    }

    onClick(event: MouseEvent) {
        this.item.click!.emit(event);

        if (this.clickToClose) {
            this.remove();
        }
    }

    onClickIcon(event: MouseEvent) {
        this.item.clickIcon!.emit(event);

        if (this.clickIconToClose) {
            this.remove();
        }
    }

    // Attach all the overrides
    attachOverrides() {
        Object.keys(this.item.override).forEach(a => {
            if (a in this) {
                (this as any)[a] = this.item.override[a];
            }
        });
    }

    private instance = () => {
        const now = new Date().getTime();

        if (this.endTime < now) {
            this.remove();
            this.item.timeoutEnd!.emit();
        } else if (!this.stopTime) {
            if (this.showProgressBar) {
                // We add this.sleepTime just to have 100% before close
                this.progressWidth = Math.min((now - this.startTime + this.sleepTime) * 100 / this.timeOut, 100);
            }

            this.timer = setTimeout(this.instance, this.sleepTime);
        }
        this.zone.run(() => {
            if (!(this.cd as ViewRef).destroyed) {
                this.cd.detectChanges();
            }
        });
    };

    protected remove() {
        if (this.animate) {
            this.item.state = this.animate + 'Out';
            setTimeout(() => {
                this.notificationService.set(this.item, false);
            }, 310);
        } else {
            this.notificationService.set(this.item, false);
        }
    }

    private contentType(item: any, key: string) {
        if (item instanceof TemplateRef) {
            this[key] = item;
        } else {
            this[key] = this.domSanitizer.bypassSecurityTrustHtml(item);
        }

        this[key + 'IsTemplate'] = item instanceof TemplateRef;
    }

    protected readonly IconsType = IconsType;
}
